/*
 * Monitor video self-heal (docs/MONITOR-VIDEO-SELFHEAL-DESIGN.md, V2/V3/V5): the words a tile shows.
 *
 * Pure and dependency-free so the texts are unit-testable and the chip, the stream and the page all
 * say the same thing for the same cause.
 *
 * Two sources of reasons:
 *   - the viewer socket's CLOSE CODE (Spring's /uiapi/bm/vview endpoint, or the browser itself);
 *   - a NOTE: a text frame {"t":"vstat", serial, state, code, reason, attempt, retryInMs, src, replay}
 *     relayed by Spring. Codes UPLINK_* / WAITING are Spring's own (S2); every other code comes from
 *     the customer's client (BM r4, B5). A launcher r3 client sends only a bare state
 *     ("open" | "error" | "closed") with no code and no reason.
 */

/** Close codes after which a tile gives up at once (remote route only). kind -> never "repair". */
export const GIVE_UP_CLOSE = Object.freeze({
  4401: { kind: 'auth', text: 'Signed out, sign in again' },
  4403: { kind: 'origin', text: 'This site may not open video (server setting)' },
  4404: { kind: 'not-owned', text: 'This PC is not on your account' },
  4429: { kind: 'caps', text: 'Too many live screens open (close another tab)' },
  4503: { kind: 'gate', text: 'Live video through the server is off' },
  1008: { kind: 'policy', text: 'The video connection was refused (a bug on our side)' },
});

/** Give-up kinds a repair cannot fix: the cause is the browser, the account or the server. */
export const NO_REPAIR_KINDS = new Set(['auth', 'origin', 'not-owned', 'caps', 'gate', 'policy', 'idle', 'signed-out']);

/** Spring's own notes (S2), sent with src:"server". Their retryInMs is the server's next word, not a PC retry. */
export const SERVER_NOTE_CODES = new Set(['UPLINK_OFFLINE', 'UPLINK_LOST', 'UPLINK_BACK', 'WAITING']);
/** The subset that means "the PC's uplink is down / being waited on": no repair offered for those. */
export const SERVER_HOLD_CODES = new Set(['UPLINK_OFFLINE', 'UPLINK_LOST', 'WAITING']);

const PC_CODES = {
  OPEN: 'Connecting to the phone...',
  INITIAL: 'Starting the picture...',
  PHLOGS_NOT_INSTALLED: 'The video helper is not installed on {pc} yet',
  PHLOGS_INSTALLING: 'Installing the video helper on {pc}',
  PHLOGS_STARTING: 'Starting the video helper on {pc}',
  PHLOGS_EXITED: 'The video helper on {pc} stopped{exit}; restarting{in}',
  PHLOGS_HUNG: 'The video helper stopped answering; restarting it',
  PHLOGS_PORT_FOREIGN: 'Another program on {pc} uses port 6847, which video needs',
  PHLOGS_FLAPPING: 'The video helper keeps crashing; paused for an hour. Press Refresh to try now',
  NODE_MISSING: 'Node.js is missing on {pc}',
  NODE_TOO_OLD: 'Node.js is too old on {pc}',
  DISK_LOW: "{pc}'s disk is full{mb}",
  ADB_PORT_MISMATCH: 'Video needs adb on port 5037; {pc} uses another port',
  CONNECT_REFUSED: 'Reconnecting on {pc}{in}',
  HANDSHAKE_FAILED: 'Reconnecting on {pc}{in}',
  NO_INITIAL: 'The phone did not start its screen stream (locked, or USB debugging needs a tap)',
  STREAM_ENDED: "The phone's stream ended; reconnecting",
  STALLED: "The phone's stream ended; reconnecting",
  // MF7 (2026-09-29): BM r4's screen check and flapping cap, and ph-logs 1.0.2's legacy-engine policy (close 4013/4014).
  SCREEN_OFF: "The phone's screen is off - the picture comes back when it is turned on",
  SCREEN_ON: "The phone's screen is on again - the picture is coming",
  FLAPPING: "The phone's stream keeps dropping right after it starts; {pc} tries again{in}",
  LEGACY_REFUSED: "{pc} may not stream this phone with the older engine (not one of its own phones, or not an older Android)",
  LEGACY_START_FAILED: "The older engine did not start on the phone; {pc} tries again{in}",
  GAVE_UP: 'Video off: {reason}',
  UPLINK_OFFLINE: 'The client on {pc} is not connected for video',
  UPLINK_LOST: 'The client on {pc} is not connected for video',
  UPLINK_BACK: 'The client on {pc} is back; starting the phone',
  WAITING: 'Waiting for the phone on {pc}...',
};

const BARE_STATE = {
  open: 'Connecting to the phone...',
  error: "The client on {pc} could not open this phone's video (older client: no reason given)",
  closed: "The phone's stream on {pc} ended; reconnecting",
};

/**
 * Review D-4: BM r4 says so when nothing will restart the helper (takeOver or watchdog off, or a relay this client did not
 * start). The tile then shows the PC's own words, never "restarting".
 */
const NOT_RESTARTED = /not restarted|not by this client|not started by this client/i;

function fill(tpl, note, pc) {
  const reason = (note && note.reason) || '';
  const secs = note && typeof note.retryInMs === 'number' && note.retryInMs >= 0
    ? Math.max(1, Math.round(note.retryInMs / 1000)) : null;
  const exit = /exit(?: code)?\s+(-?\d+)/i.exec(reason);
  const mb = /(\d+)\s*MB/i.exec(reason);
  return tpl
    .replace(/\{pc\}/g, pc || 'the PC')
    .replace(/\{in\}/g, secs != null ? ` in ${secs} s` : '')
    .replace(/\{exit\}/g, exit ? ` (exit ${exit[1]})` : '')
    .replace(/\{mb\}/g, mb ? ` (${mb[1]} MB free)` : '')
    .replace(/\{reason\}/g, reason || 'the client gave up');
}

/** The tile text for a note. Unknown codes fall back to the server's own reason. */
export function noteText(note, pc) {
  if (!note) return '';
  const code = note.code ? String(note.code).toUpperCase() : '';
  if ((code === 'PHLOGS_HUNG' || code === 'PHLOGS_EXITED') && note.reason && NOT_RESTARTED.test(note.reason)) {
    return `${fill('{pc}: ', note, pc)}${note.reason}`;
  }
  if (code && PC_CODES[code]) return fill(PC_CODES[code], note, pc);
  if (code) return note.reason ? `${fill('{pc}: ', note, pc)}${note.reason}` : `${code} on ${pc || 'the PC'}`;
  const st = note.state ? String(note.state).toLowerCase() : '';
  if (BARE_STATE[st]) return fill(BARE_STATE[st], note, pc);
  return note.reason || '';
}

/** Why a socket closed, in words. `route` is 'local' (direct) or 'remote' (through the server). */
export function closeText(code, reason, route) {
  const g = route === 'remote' ? GIVE_UP_CLOSE[code] : null;
  if (g) return g.text;
  if (code === 1009) return 'Your connection was too slow, reconnecting';
  if (code === 1006 || code == null) {
    return route === 'remote'
      ? 'Lost the connection to the server'
      : 'Cannot reach the video helper on this computer';
  }
  if (reason) return `Closed (${code}): ${String(reason).slice(0, 120)}`;
  return `The connection closed (${code})`;
}

/** Does the last reason come from the customer's PC, so that "Repair video on {pc}" can help? */
export function isPcReason(status) {
  if (!status) return false;
  if (status.gaveUp && NO_REPAIR_KINDS.has(status.gaveUp)) return false;
  const n = status.note;
  if (!n) return false;
  const code = n.code ? String(n.code).toUpperCase() : '';
  if (n.src === 'server' || SERVER_NOTE_CODES.has(code)) return false;
  if (/^(PHLOGS_|DISK_LOW|NODE_)/.test(code) || code === 'GAVE_UP') return true;
  return !code && String(n.state || '').toLowerCase() === 'error';
}

/** A tile that is not streaming and whose cause a PC repair could plausibly fix (V4 step 3). */
export function wantsRepair(status) {
  if (!status) return true; // no status yet = not streaming
  if (status.state === 'streaming') return false;
  if (status.gaveUp && NO_REPAIR_KINDS.has(status.gaveUp)) return false;
  // MF7: a repair cannot switch a phone's screen on - its PC already says so, and the picture returns by itself.
  if (status.note && String(status.note.code || '').toUpperCase() === 'SCREEN_OFF') return false;
  return true;
}
